import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { Router, RouterLink } from "@angular/router";
import { DefaultComponent } from "src/app/default.component";
import { FileManager } from "src/classes/FilesManager";
import { ROUTES_CONFIG } from "src/config/routes.config";
import { LinkInterceptorDirective } from "src/directives/link-interceptor.directive";
import { ActionResponse, Message } from "src/interfaces/post-request/post-request";
import { IconReplacePipe } from "src/pipes/icon-replace.pipe";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { PrefixTemplate } from "../../PrefixTemplate";
import { LinkData } from "../template-link/template-link.component";

enum Severity {
  LOW,
  MINOR,
  MAJOR,
  CRITICAL,
}

@Component({
  selector: "app-template-notification",
  imports: [CommonModule, IconReplacePipe, RouterLink, MatButtonModule, LinkInterceptorDirective],
  templateUrl: "./template-notification.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./template-notification.component.less",
})
export class TemplateNotificationComponent<CONTROL, VALUE = CONTROL> extends DefaultComponent implements PrefixTemplate<CONTROL, VALUE>, OnInit {
  @Input({ required: true })
  public control: FormControl<CONTROL> | null;

  @Input()
  public value: VALUE | null;

  @Input()
  public label: string | null;

  @Input()
  public severity: Severity;

  @Input()
  public links: LinkData[];

  @Input()
  public sslinks: LinkData[];

  private router: Router;
  private http = inject(HttpService);
  private application = inject(ApplicationService);

  public constructor() {
    super();
    this.router = inject(Router);
    this.control = null;
    this.value = null;
    this.label = null;
    this.links = [];
    this.sslinks = [];
    this.severity = Severity.LOW;
  }

  public ngOnInit(): void {
    if (!this.control) throw new Error("Undefined control");
  }

  /**
   * Returns the key of Severity
   * @param severity
   * @returns
   */
  public getSeverityAsString(severity: Severity): string {
    return Severity[severity];
  }

  public createIconElement(iconClassList: string): string {
    return `<i class="${iconClassList}"></i>`;
  }

  public onLinkClicked(link: LinkData): Promise<void> {
    switch (link.type) {
      case "1":
        return this.onSnapshot(link.link, null);
      case "2":
        return this.sendAction(link);
      default:
        console.error("Link of type", link.type, "is not supported yet.");
        return new Promise((_, reject) => reject());
    }
  }

  private async sendAction(route: LinkData): Promise<void> {
    const id = route.link;
    const res = await this.http.send<ActionResponse>(ROUTES_CONFIG.actionurl, {
      FFWDActionID: id,
    });
    this.application.onMessage(
      typeof res.postActionAsJSON === "string" ? JSON.parse(res.postActionAsJSON) : (<{ messages: Message[] }>res.postActionAsJSON).messages,
    );
  }

  private async onSnapshot(actionid: string, filename: string | null): Promise<void> {
    try {
      const headers = new Headers();
      const response = await this.actionObserve<Blob>(actionid, "", "blob", headers);

      if (!response) throw new Error("Unable to download. Response body was empty");

      FileManager.download({
        blob: response,
        label: this.getFilenameFromHeader(headers) ?? "null",
      });
    } catch (e) {
      console.error("Unable to snapshot", e);
    }
  }

  protected getFilenameFromHeader(headers: Headers): string | null {
    const header = headers.get("Content-Disposition");
    const start = /filename=(")?/;
    const end = /(")?;/;
    if (header) {
      return header.split(start).pop()?.split(end).shift() || null;
    }
    return null;
  }

  private async actionObserve<T>(actionid: string, actiondata = "", responseType = "json", headersOut?: Headers): Promise<T> {
    return this.http.send(
      ROUTES_CONFIG.actionurl,
      {
        FFWDActionID: actionid,
        actionData: actiondata,
      },
      {
        responseType: <"json">responseType,
        observe: <"body">"response",
        responseHeadersOut: headersOut,
      },
    );
  }
}
